import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Modal,
  ModalActions,
  NotProvided,
  Page,
  PageHeader,
  Select,
  Table,
} from '@evogard/ui';
import type { PaymentAccount } from '@evogard/contract';
import { PAYMENT_ACCOUNT_PROVIDER_LABELS } from '@evogard/domain';
import { api, ok } from '../../../lib/api.js';
import { can, useSessionContext } from '../../../lib/session.js';
import { REVENUES_KEY } from './RevenuesScreen.js';

/**
 * As contas de recebimento da base (29/10/2026) — por onde o dinheiro entra.
 *
 * Hoje é uma por praça, a do Asaas, e toda receita usa ela. A tela edita o
 * nome e a conta financeira do Conta Azul onde a receita dela será lançada.
 * Cadastrar a segunda conta, e escolher conta por tipo de receita, fica para
 * quando a segunda aparecer (documento "Receita no Financeiro", seção Depois).
 * A chave do Asaas continua em Plataforma › Integrações.
 */
export function PaymentAccountsScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const canConfigure = can(context, 'finance.revenues', 'configurar');
  const [editing, setEditing] = useState<PaymentAccount | null>(null);

  const query = useQuery({
    queryKey: [...REVENUES_KEY, tenantId, 'contas'],
    queryFn: async () =>
      ok(
        await api.revenues.listPaymentAccounts(),
        200,
        'Não foi possível carregar as contas de recebimento.',
      ),
    enabled: tenantId !== undefined,
  });

  return (
    <Page measure="cheia">
      <PageHeader title="Contas de recebimento">
        Por onde o dinheiro da receita entra. Toda receita desta base usa a conta padrão;
        a chave do Asaas fica em Plataforma › Integrações.
      </PageHeader>

      {/*
       * Pedido do dono (29/10/2026): a tela diz que está pela metade. O que
       * falta é a seção "Depois" do documento "Receita no Financeiro".
       */}
      <Alert tone="alerta" title="Tela e funcionalidade em desenvolvimento.">
        Hoje cada base tem uma conta só, a do Asaas, e toda receita usa ela. A ideia é
        poder cadastrar mais de uma conta, em bancos diferentes, e definir qual conta é
        usada em cada evento — por exemplo, a PI de Periféricos por uma conta e a receita
        avulsa por outra. Por enquanto, dá para mudar só o nome e a conta no Conta Azul.
      </Alert>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {query.data && query.data.contaazulAccounts.length === 0 && (
        <Alert tone="alerta">
          Esta base não tem as contas financeiras do Conta Azul cadastradas, e a conta de
          recebimento fica sem conta de lançamento.
        </Alert>
      )}

      {(!query.isError || query.data) && (
        <Card bleed>
          <Table<PaymentAccount>
            label="Contas de recebimento"
            items={query.data?.items ?? []}
            loading={query.isLoading}
            rowKey={(a) => String(a.id)}
            empty="Nenhuma conta de recebimento nesta base."
            columns={[
              {
                title: 'Conta',
                minWidth: '14rem',
                cell: (a) => (
                  <span className="flex items-center gap-2">
                    <span className="text-texto">{a.name}</span>
                    {a.isDefault && <Badge tone="informacao">Padrão</Badge>}
                  </span>
                ),
              },
              {
                title: 'Provedor',
                cell: (a) => PAYMENT_ACCOUNT_PROVIDER_LABELS[a.provider],
              },
              {
                title: 'Conta no Conta Azul',
                minWidth: '14rem',
                cell: (a) => a.contaazulAccount?.name ?? <NotProvided />,
              },
              {
                title: 'Ações',
                hiddenLabel: 'Ações',
                alignment: 'fim',
                cell: (a) =>
                  canConfigure ? (
                    <Button variant="contorno" size="sm" onClick={() => setEditing(a)}>
                      Editar
                    </Button>
                  ) : null,
              },
            ]}
          />
        </Card>
      )}

      {editing && query.data && (
        <EditModal
          account={editing}
          options={query.data.contaazulAccounts}
          onClose={() => setEditing(null)}
        />
      )}
    </Page>
  );
}

function EditModal({
  account,
  options,
  onClose,
}: {
  account: PaymentAccount;
  options: { id: string; name: string }[];
  onClose: () => void;
}) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const client = useQueryClient();
  const [name, setName] = useState(account.name);
  const [contaazul, setContaazul] = useState(account.contaazulAccount?.id ?? '');
  const [submitted, setSubmitted] = useState(false);
  const problem = name.trim().length >= 2 ? null : 'Escreva o nome.';

  const save = useMutation({
    mutationFn: async () =>
      ok(
        await api.revenues.updatePaymentAccount({
          params: { id: account.id },
          body: { name: name.trim(), contaazulAccountId: contaazul || null },
        }),
        200,
        'Não foi possível salvar a conta.',
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: [...REVENUES_KEY, tenantId] });
      onClose();
    },
  });

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Editar conta de recebimento"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(true);
          if (!problem) save.mutate();
        }}
      >
        {save.error && <Alert tone="erro">{save.error.message}</Alert>}
        <Field
          label="Nome"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          error={submitted ? problem : null}
        />
        <Select
          label="Conta no Conta Azul"
          value={contaazul}
          onChange={(e) => setContaazul(e.target.value)}
          help="A conta financeira onde a receita desta conta é lançada no Conta Azul."
        >
          <option value="">Nenhuma</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </Select>
        <ModalActions>
          <Button type="button" variant="contorno" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={save.isPending}>
            Salvar
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
}
